import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';

interface FileItem {
  id: string;
  name: string;
  type: 'file' | 'folder';
  size?: number;
  modified: Date;
  parent_folder?: string;
  business_id: string;
  file_extension?: string;
  storage_path?: string;
  created_at: string;
  updated_at: string;
}

interface CreateFolderRequest {
  name: string;
  parentFolder?: string | null;
}

interface UploadFilesRequest {
  files: File[];
  parentFolder?: string | null;
}

interface FileDownloadResponse {
  signed_url: string;
  expires_in: number;
  filename: string;
  mime_type?: string;
}

const BUSINESS_FILES_KEY = 'business-files';

export function useBusinessFiles(businessId: string) {
  const queryClient = useQueryClient();
  const supabase = createClient();

  // Fetch files for business
  const filesQuery = useQuery({
    queryKey: [BUSINESS_FILES_KEY, businessId],
    queryFn: async (): Promise<FileItem[]> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token available');
      }

      // Use backend API to get files
      const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/files/business/${businessId}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch files: ${errorText}`);
      }

      const businessFiles = await response.json();
      
      // Transform backend response to match FileItem interface
      const files: FileItem[] = businessFiles.map((item: any) => ({
        id: item.id,
        name: item.name,
        type: item.type,
        size: item.file_size || 0,
        modified: new Date(item.updated_at),
        parent_folder: item.parent_folder,
        business_id: item.business_id,
        file_extension: item.file_extension,
        storage_path: item.storage_path,
        created_at: item.created_at,
        updated_at: item.updated_at,
      }));

      return files;
    },
    enabled: !!businessId,
  });

  // Download file function
  const downloadFile = async (fileId: string): Promise<string> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('No access token available');
    }

    const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/files/download/${fileId}`, {
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get download URL: ${errorText}`);
    }

    const downloadData: FileDownloadResponse = await response.json();
    return downloadData.signed_url;
  };

  // Create folder mutation
  const createFolderMutation = useMutation({
    mutationFn: async ({ name, parentFolder }: CreateFolderRequest) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token available');
      }

      const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/files/folders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          name,
          business_id: businessId,
          parent_folder: parentFolder,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Create folder failed: ${errorText}`);
      }

      const result = await response.json();
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BUSINESS_FILES_KEY, businessId] });
    },
  });

  // Upload files mutation
  const uploadFilesMutation = useMutation({
    mutationFn: async ({ files, parentFolder }: UploadFilesRequest) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token available');
      }

      // Use backend API for file upload
      const formData = new FormData();
      formData.append('business_id', businessId);
      if (parentFolder) {
        formData.append('parent_folder', parentFolder);
      }
      
      files.forEach((file) => {
        formData.append('files', file);
      });

      const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/files/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${errorText}`);
      }

      const result = await response.json();
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BUSINESS_FILES_KEY, businessId] });
    },
  });

  // Delete files mutation
  const deleteFilesMutation = useMutation({
    mutationFn: async (fileIds: string[]) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token available');
      }

      // Use backend API for file deletion
      const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/files/files`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(fileIds),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Delete failed: ${errorText}`);
      }

      const result = await response.json();
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BUSINESS_FILES_KEY, businessId] });
      toast.success('Files deleted successfully');
    },
    onError: (error) => {
      console.error('Delete files error:', error);
      toast.error('Failed to delete files');
    },
  });

  return {
    data: filesQuery.data,
    isLoading: filesQuery.isLoading,
    error: filesQuery.error,
    createFolder: createFolderMutation,
    uploadFiles: uploadFilesMutation,
    deleteFiles: deleteFilesMutation,
    downloadFile,
    isCreatingFolder: createFolderMutation.isPending,
    isUploading: uploadFilesMutation.isPending,
    isDeleting: deleteFilesMutation.isPending,
  };
}
